import React, { useEffect, useMemo, useState } from "react";
import {
  FaCalendarAlt,
  FaLayerGroup,
  FaMapSigns,
  FaSearch,
  FaUserTie,
  FaUsers,
  FaCheckCircle,
  FaTimesCircle,
} from "react-icons/fa";
import { toast } from "react-toastify";

import Button from "../../../components/Button/Button";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "../../../components/Modal/Modal";
import { getAllActeursListWithGroup } from "./../../../services/acteursServices";
import {
  getAllActorsByEventId,
  syncActorsSelectionForEvent,
} from "./../../../services/acteureventServices";
import { sendNotification } from "../../../services/notificationServices";
import { getUserIdByActeurId } from "./../../../services/userServices";
import formatDate from "./../../../utils/formatDate";

const POSTES = [
  "Organisateur",
  "Responsable Ligue",
  "Responsable Comité",
  "Bénévolontaire",
  "Responsable Arbitrage",
  "Notateur",
  "Arbitre",
  "Commissaire Sportif",
  "Juge",
];

const TAPS_REQUIRED_POSTES = ["Arbitre", "Commissaire Sportif"];

const normalize = (value) =>
  (value || "")
    .toString()
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase()
    .trim();

const formatTapisValue = (value) => {
  const cleaned = (value || "").toString().trim();
  if (!cleaned) return "";
  if (/^\d+$/.test(cleaned)) return `Tapis ${cleaned}`;
  return cleaned;
};

const getEventActorsArray = (payload) => {
  if (Array.isArray(payload?.Acteur)) return payload.Acteur;
  if (Array.isArray(payload?.Acteurs)) return payload.Acteurs;
  if (Array.isArray(payload?.acteurs)) return payload.acteurs;
  return [];
};

const ActorGroupSelectionModal = ({
  isOpen,
  onClose,
  eventId,
  eventTitle,
  eventStart,
  refreshList,
}) => {
  const [acteurs, setActeurs] = useState([]);
  const [assignedActorIds, setAssignedActorIds] = useState([]);
  const [selectedGroupIds, setSelectedGroupIds] = useState([]);
  const [selectionByActorId, setSelectionByActorId] = useState({});
  const [poste, setPoste] = useState("");
  const [tapis, setTapis] = useState("");
  const [filter, setFilter] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const titleId = "actor-group-selection-modal-title";
  const descriptionId = "actor-group-selection-modal-description";

  const needsTapis = TAPS_REQUIRED_POSTES.includes(poste);

  useEffect(() => {
    if (!isOpen || !eventId) return;

    const fetchData = async () => {
      try {
        setLoading(true);
        setActeurs([]);
        setAssignedActorIds([]);
        setSelectedGroupIds([]);
        setSelectionByActorId({});
        setPoste("");
        setTapis("");
        setFilter("");

        const [acteursData, eventActeursData] = await Promise.all([
          getAllActeursListWithGroup(),
          getAllActorsByEventId(eventId),
        ]);

        const sortedActeurs = (acteursData || []).sort((a, b) =>
          `${a.nom || ""} ${a.prenom || ""}`
            .toLowerCase()
            .localeCompare(
              `${b.nom || ""} ${b.prenom || ""}`.toLowerCase(),
              "fr",
            ),
        );

        const eventActeurs = getEventActorsArray(eventActeursData);

        setActeurs(sortedActeurs);
        setAssignedActorIds(eventActeurs.map((acteur) => String(acteur.id)));
      } catch (error) {
        console.error("Erreur chargement convocation par groupes:", error);
        toast.error("Erreur lors du chargement des données.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [isOpen, eventId]);

  useEffect(() => {
    if (!needsTapis) {
      setTapis("");
    }
  }, [needsTapis]);

  const assignedSet = useMemo(
    () => new Set(assignedActorIds.map(String)),
    [assignedActorIds],
  );

  const groupOptions = useMemo(() => {
    const map = new Map();

    acteurs.forEach((acteur) => {
      (acteur.Groups || []).forEach((group) => {
        if (!group?.id) return;

        const key = String(group.id);
        const current = map.get(key) || {
          id: key,
          nom: group.nom || `Groupe ${group.id}`,
          count: 0,
        };
        current.count += 1;
        map.set(key, current);
      });
    });

    return Array.from(map.values()).sort((a, b) =>
      a.nom.localeCompare(b.nom, "fr"),
    );
  }, [acteurs]);

  const scopeActors = useMemo(() => {
    if (!selectedGroupIds.length) return [];

    return acteurs.filter((acteur) =>
      (acteur.Groups || []).some((group) =>
        selectedGroupIds.includes(String(group.id)),
      ),
    );
  }, [acteurs, selectedGroupIds]);

  useEffect(() => {
    if (!scopeActors.length) return;

    setSelectionByActorId((prev) => {
      const next = { ...prev };

      scopeActors.forEach((acteur) => {
        const key = String(acteur.id);
        if (next[key] === undefined) {
          next[key] = true;
        }
      });

      return next;
    });
  }, [scopeActors]);

  const displayedActors = useMemo(() => {
    if (!filter) return scopeActors;

    const search = normalize(filter);

    return scopeActors.filter((acteur) => {
      const fullName = normalize(`${acteur.nom} ${acteur.prenom}`);
      const club = normalize(acteur.club_acteur);
      const groups = normalize(
        (acteur.Groups || []).map((g) => g.nom).join(" "),
      );

      return (
        fullName.includes(search) ||
        club.includes(search) ||
        groups.includes(search)
      );
    });
  }, [scopeActors, filter]);

  const selectedScopeActorIds = useMemo(() => {
    return scopeActors
      .filter((acteur) => selectionByActorId[String(acteur.id)] !== false)
      .map((acteur) => Number(acteur.id));
  }, [scopeActors, selectionByActorId]);

  const createdPreviewCount = useMemo(() => {
    return selectedScopeActorIds.filter((id) => !assignedSet.has(String(id)))
      .length;
  }, [selectedScopeActorIds, assignedSet]);

  const removedPreviewCount = useMemo(() => {
    return scopeActors.filter(
      (acteur) =>
        assignedSet.has(String(acteur.id)) &&
        selectionByActorId[String(acteur.id)] === false,
    ).length;
  }, [scopeActors, assignedSet, selectionByActorId]);

  const keptPreviewCount = useMemo(() => {
    return selectedScopeActorIds.filter((id) => assignedSet.has(String(id)))
      .length;
  }, [selectedScopeActorIds, assignedSet]);

  const toggleGroup = (groupId) => {
    setSelectedGroupIds((prev) =>
      prev.includes(String(groupId))
        ? prev.filter((id) => id !== String(groupId))
        : [...prev, String(groupId)],
    );
  };

  const toggleActor = (acteurId) => {
    const key = String(acteurId);
    setSelectionByActorId((prev) => ({
      ...prev,
      [key]: prev[key] === false,
    }));
  };

  const setDisplayedActorsChecked = (checked) => {
    setSelectionByActorId((prev) => {
      const next = { ...prev };

      displayedActors.forEach((acteur) => {
        next[String(acteur.id)] = checked;
      });

      return next;
    });
  };

  const handleSubmit = async () => {
    if (!poste) {
      toast.error("Merci de sélectionner un poste.");
      return;
    }

    if (!selectedGroupIds.length) {
      toast.error("Merci de sélectionner au moins un groupe.");
      return;
    }

    if (!scopeActors.length) {
      toast.error("Aucun acteur trouvé dans les groupes sélectionnés.");
      return;
    }

    // if (needsTapis && !tapis.trim()) {
    //   toast.error("Merci de renseigner le tapis.");
    //   return;
    // }

    if (createdPreviewCount === 0 && removedPreviewCount === 0) {
      toast.info("Aucune modification à enregistrer.");
      return;
    }

    try {
      setSubmitting(true);

      const formattedTapis = needsTapis ? formatTapisValue(tapis) : null;

      const result = await syncActorsSelectionForEvent({
        eventId,
        acteurIds: selectedScopeActorIds,
        scopeActeurIds: scopeActors.map((acteur) => Number(acteur.id)),
        poste,
        tapis: formattedTapis,
      });

      const createdIds = Array.isArray(result?.createdActeurIds)
        ? result.createdActeurIds
        : [];

      if (createdIds.length > 0) {
        const message = `Le Responsable de la Manifestation ou le Responsable d'arbitrage vous a convoqué sur l'événement : "${eventTitle}" le ${formatDate(
          eventStart,
        )} en tant que ${poste}${
          formattedTapis ? ` (${formattedTapis})` : ""
        }.`;

        await Promise.all(
          createdIds.map(async (acteurId) => {
            const users = await getUserIdByActeurId(acteurId);

            await Promise.all(
              (users || []).map((user) =>
                sendNotification(message, user.user_id),
              ),
            );
          }),
        );
      }

      toast.success(
        `Synchronisation terminée : ${result?.createdCount || 0} ajout(s), ${
          result?.removedCount || 0
        } retrait(s).`,
      );

      await refreshList?.();
      onClose?.();
    } catch (error) {
      console.error("Erreur synchronisation groupes :", error);
      toast.error("Erreur lors de la synchronisation des convocations.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="xl"
      labelledById={titleId}
      describedById={descriptionId}
      initialFocusSelector='input[name="group-actor-filter"]'
    >
      <ModalHeader
        id={titleId}
        title="Convoquer par groupes"
        subtitle="Sélection multiple des acteurs sur un événement"
        onClose={onClose}
      />

      <ModalBody id={descriptionId}>
        <div className="space-y-5">
          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-700 dark:bg-slate-800/70">
            <div className="flex items-start gap-3">
              <div className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-blue-100 text-blue-700 dark:bg-blue-500/15 dark:text-blue-300">
                <FaCalendarAlt />
              </div>

              <div className="min-w-0 flex-1">
                <p className="text-[11px] uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
                  Événement
                </p>

                <div className="mt-1 text-base font-semibold text-slate-900 dark:text-white">
                  {eventTitle || "Événement non trouvé"}
                </div>

                {eventStart && (
                  <div className="mt-1 text-sm text-slate-600 dark:text-slate-300">
                    Date :{" "}
                    <span className="font-medium">
                      {formatDate(eventStart)}
                    </span>
                  </div>
                )}

                <p className="mt-2 text-xs italic text-slate-500 dark:text-slate-400">
                  Le système synchronise uniquement les acteurs appartenant aux
                  groupes sélectionnés.
                </p>
              </div>
            </div>
          </div>

          <div className="grid gap-4 lg:grid-cols-2">
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-700 dark:text-slate-200">
                Poste sur l’événement
              </label>

              <div className="relative">
                <FaUserTie className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <select
                  value={poste}
                  onChange={(e) => setPoste(e.target.value)}
                  className="h-11 w-full rounded-2xl border border-slate-300 bg-white pl-10 pr-4 text-sm text-slate-900 outline-none transition focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
                >
                  <option value="">Sélectionner un poste</option>
                  {POSTES.map((item) => (
                    <option key={item} value={item}>
                      {item}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {needsTapis && (
              <div>
                <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-slate-700 dark:text-slate-200">
                  Tapis{" "}
                  <span className="normal-case text-slate-400">
                    (optionnel)
                  </span>
                </label>

                <div className="relative">
                  <FaMapSigns className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    type="text"
                    value={tapis}
                    onChange={(e) => setTapis(e.target.value)}
                    placeholder="Ex : 1, 2, 3 ou Tapis 1"
                    className="h-11 w-full rounded-2xl border border-slate-300 bg-white pl-10 pr-4 text-sm text-slate-900 outline-none transition focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
                  />
                </div>
              </div>
            )}
          </div>

          <div>
            <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-slate-700 dark:text-slate-200">
              Groupes
            </label>

            <div className="flex flex-wrap gap-2">
              {groupOptions.map((group) => {
                const active = selectedGroupIds.includes(String(group.id));

                return (
                  <button
                    key={group.id}
                    type="button"
                    onClick={() => toggleGroup(group.id)}
                    className={`inline-flex items-center gap-2 rounded-full border px-3 py-2 text-sm transition ${
                      active
                        ? "border-indigo-500 bg-indigo-50 text-indigo-700 dark:border-indigo-400 dark:bg-indigo-500/10 dark:text-indigo-300"
                        : "border-slate-300 bg-white text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200 dark:hover:bg-slate-800"
                    }`}
                  >
                    <FaLayerGroup className="text-xs" />
                    <span>{group.nom}</span>
                    <span className="rounded-full bg-white/80 px-2 py-0.5 text-[11px] dark:bg-slate-900/60">
                      {group.count}
                    </span>
                  </button>
                );
              })}

              {!groupOptions.length && !loading && (
                <div className="text-sm text-slate-500">
                  Aucun groupe disponible.
                </div>
              )}
            </div>
          </div>

          <div className="grid gap-3 lg:grid-cols-[minmax(0,1fr),auto,auto]">
            <div className="relative">
              <FaSearch className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                data-autofocus
                name="group-actor-filter"
                type="text"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                placeholder="Nom, prénom, club, groupe…"
                className="h-11 w-full rounded-2xl border border-slate-300 bg-white pl-10 pr-4 text-sm text-slate-900 outline-none transition focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
              />
            </div>

            <Button
              text="Tout cocher"
              onClick={() => setDisplayedActorsChecked(true)}
              className="!rounded-2xl !bg-emerald-600 hover:!bg-emerald-700"
            />

            <Button
              text="Tout décocher"
              onClick={() => setDisplayedActorsChecked(false)}
              className="!rounded-2xl !bg-amber-500 hover:!bg-amber-600"
            />
          </div>

          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <div className="rounded-2xl border border-slate-200 bg-white px-4 py-3 dark:border-slate-700 dark:bg-slate-950">
              <div className="text-[11px] uppercase tracking-[0.16em] text-slate-500">
                Périmètre
              </div>
              <div className="mt-1 flex items-center gap-2 text-lg font-bold text-slate-900 dark:text-white">
                <FaUsers className="text-slate-400" />
                {scopeActors.length}
              </div>
            </div>

            <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 dark:border-emerald-500/20 dark:bg-emerald-500/10">
              <div className="text-[11px] uppercase tracking-[0.16em] text-emerald-700 dark:text-emerald-300">
                À ajouter
              </div>
              <div className="mt-1 flex items-center gap-2 text-lg font-bold text-emerald-800 dark:text-emerald-200">
                <FaCheckCircle />
                {createdPreviewCount}
              </div>
            </div>

            <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 dark:border-amber-500/20 dark:bg-amber-500/10">
              <div className="text-[11px] uppercase tracking-[0.16em] text-amber-700 dark:text-amber-300">
                Conservés
              </div>
              <div className="mt-1 text-lg font-bold text-amber-800 dark:text-amber-200">
                {keptPreviewCount}
              </div>
            </div>

            <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 dark:border-rose-500/20 dark:bg-rose-500/10">
              <div className="text-[11px] uppercase tracking-[0.16em] text-rose-700 dark:text-rose-300">
                À retirer
              </div>
              <div className="mt-1 flex items-center gap-2 text-lg font-bold text-rose-800 dark:text-rose-200">
                <FaTimesCircle />
                {removedPreviewCount}
              </div>
            </div>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-950">
            <div className="border-b border-slate-200 px-4 py-3 text-sm text-slate-600 dark:border-slate-700 dark:text-slate-300">
              {loading
                ? "Chargement des acteurs…"
                : selectedGroupIds.length === 0
                  ? "Sélectionnez au moins un groupe."
                  : `${displayedActors.length} acteur(s) affiché(s) • ${scopeActors.length} acteur(s) dans le périmètre`}
            </div>

            <div className="max-h-[420px] overflow-y-auto">
              {!loading && selectedGroupIds.length === 0 ? (
                <div className="px-4 py-8 text-center text-sm text-slate-500">
                  Aucun groupe sélectionné.
                </div>
              ) : !loading && displayedActors.length === 0 ? (
                <div className="px-4 py-8 text-center text-sm text-slate-500">
                  Aucun acteur ne correspond à la recherche.
                </div>
              ) : (
                displayedActors.map((acteur) => {
                  const actorId = String(acteur.id);
                  const isChecked = selectionByActorId[actorId] !== false;
                  const isAlreadyAssigned = assignedSet.has(actorId);

                  return (
                    <label
                      key={acteur.id}
                      className="flex cursor-pointer items-start gap-3 border-b border-slate-100 px-4 py-3 last:border-b-0 hover:bg-slate-50 dark:border-slate-800 dark:hover:bg-slate-900/60"
                    >
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={() => toggleActor(acteur.id)}
                        className="mt-1 h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                      />

                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <div className="font-semibold text-slate-900 dark:text-white">
                            {acteur.nom?.toUpperCase()} {acteur.prenom}
                          </div>

                          {isAlreadyAssigned && (
                            <span className="rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-700 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-300">
                              Déjà convoqué
                            </span>
                          )}
                        </div>

                        {acteur.club_acteur && (
                          <div className="mt-1 text-xs text-slate-600 dark:text-slate-300">
                            Club : {acteur.club_acteur}
                          </div>
                        )}

                        {(acteur.Groups || []).length > 0 && (
                          <div className="mt-2 flex flex-wrap gap-1.5">
                            {acteur.Groups.map((group) => {
                              const selected = selectedGroupIds.includes(
                                String(group.id),
                              );

                              return (
                                <span
                                  key={`${acteur.id}-${group.id}`}
                                  className={`rounded-full px-2.5 py-1 text-[11px] ${
                                    selected
                                      ? "bg-indigo-50 text-indigo-700 dark:bg-indigo-500/10 dark:text-indigo-300"
                                      : "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300"
                                  }`}
                                >
                                  {group.nom}
                                </span>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </label>
                  );
                })
              )}
            </div>
          </div>

          <div className="text-xs text-slate-500 dark:text-slate-400">
            La recherche agit uniquement sur l’affichage. La synchronisation
            finale s’applique à tous les acteurs des groupes sélectionnés.
          </div>
        </div>
      </ModalBody>

      <ModalFooter>
        <Button
          onClick={onClose}
          className="!rounded-2xl !bg-slate-100 !text-slate-800 hover:!bg-slate-200 dark:!bg-slate-800 dark:!text-slate-100 dark:hover:!bg-slate-700"
          text="Fermer"
        />

        <Button
          onClick={handleSubmit}
          className="!rounded-2xl"
          text={submitting ? "Synchronisation..." : "Enregistrer"}
          disabled={submitting || loading}
        />
      </ModalFooter>
    </Modal>
  );
};

export default ActorGroupSelectionModal;
